import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import api from '../common/api';
import FacilityAnisSection from '../facility-ani/FacilityAnisSection';
import { useFacilityNumbersList } from '../hooks/FacilityNumber';
import { useNotifyTabLoading } from '../hooks/useTabLoadingReporter';
import { useAlerts } from '../alert/AlertContext';
import type { FacilityNumberEditable } from '../hooks/FacilityNumber';

type FacilityNumbersTabProps = {
  facility: { id: number };
};

type OutboundRow = FacilityNumberEditable & { _client?: string };

const FacilityNumbersTab: React.FC<FacilityNumbersTabProps> = ({ facility }) => {
  const clientSeq = useRef(0);
  const [aniSectionBusy, setAniSectionBusy] = useState(false);
  const alerts = useAlerts();

  const {
    execute: refetchOutbound,
    result: apiOutbound,
    loading: outboundLoading,
    error: outboundError,
  } = useFacilityNumbersList(facility.id);

  const [outRows, setOutRows] = useState<OutboundRow[]>([]);
  const [outEditing, setOutEditing] = useState<Set<string>>(() => new Set());
  const [outSaving, setOutSaving] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (apiOutbound != null) {
      setOutRows(apiOutbound);
      setOutEditing(new Set());
      setOutSaving(new Set());
    }
  }, [apiOutbound]);

  useEffect(() => {
    if (outboundError != null) {
      alerts.error('An error occurred while loading numbers');
    }
  }, [outboundError]);

  useNotifyTabLoading('numbers', outboundLoading || outSaving.size > 0 || aniSectionBusy);

  const addOutbound = useCallback(() => {
    const row: OutboundRow = {
      ani: '',
      numberType: 1,
      active: true,
      _client: `${++clientSeq.current}`,
    };
    setOutRows((r) => [row, ...r]);
    const k = row.id != null ? `id:${row.id}` : `c:${row._client ?? ''}`;
    setOutEditing((e) => new Set(e).add(k));
  }, []);

  const startEditOutbound = useCallback((row: OutboundRow) => {
    const k = row.id != null ? `id:${row.id}` : `c:${row._client ?? ''}`;
    setOutEditing((e) => new Set(e).add(k));
  }, []);

  const setOutboundField = useCallback(
    (k: string, patch: Partial<OutboundRow>) => {
      setOutRows((prev) =>
        prev.map((r) => {
          const rk = r.id != null ? `id:${r.id}` : `c:${r._client ?? ''}`;
          return rk === k ? { ...r, ...patch } : r;
        })
      );
    },
    []
  );

  const saveOutbound = useCallback(
    (row: OutboundRow) => {
      const k = row.id != null ? `id:${row.id}` : `c:${row._client ?? ''}`;
      if (row.ani.trim() === '') {
        alerts.error('ANI is required');
        return;
      }
      const payload: Record<string, unknown> = {
        ani: row.ani.trim(),
        numberType: Number(row.numberType) || 1,
        active: row.active,
      };
      if (row.id != null) {
        payload.id = row.id;
      }

      setOutSaving((s) => new Set(s).add(k));

      const done = () => {
        setOutSaving((s) => {
          const n = new Set(s);
          n.delete(k);
          return n;
        });
      };

      const p =
        row.id != null
          ? api.put(`/facility/${facility.id}/number/${row.id}`, payload)
          : api.post(`/facility/${facility.id}/number`, payload);

      void p.then(
        () => {
          alerts.success('The number was saved');
          setOutEditing((e) => {
            const n = new Set(e);
            n.delete(k);
            return n;
          });
          void refetchOutbound(facility.id);
          done();
        },
        () => {
          alerts.error('An error occurred while saving the number');
          done();
        }
      );
    },
    [facility.id, refetchOutbound]
  );

  return (
    <div className="jarviswidget jarviswidget-color-blueDark">
      <div className="content">
        <Row>
          <Col lg={6}>
            <Button
              type="button"
              variant="primary"
              className="btn-labeled mb-2"
              disabled={outboundLoading}
              onClick={addOutbound}>
              <span className="btn-label">
                <i className="fas fa-plus-circle" />{' '}
              </span>
              Add Outbound Number
            </Button>
            {outboundLoading && outRows.length === 0 && <p className="text-muted">Loading…</p>}
            {outRows.length > 0 && (
              <table className="table table-striped table-hover table-bordered">
                <thead>
                  <tr>
                    <th>ANI</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {outRows.map((row) => {
                    const k = row.id != null ? `id:${row.id}` : `c:${row._client ?? ''}`;
                    const edit = outEditing.has(k);
                    const sav = outSaving.has(k);
                    return (
                      <tr key={k}>
                        <td>
                          <Form.Control
                            value={row.ani}
                            disabled={!edit || sav}
                            required
                            onChange={(e) => setOutboundField(k, { ani: e.target.value })}
                          />
                        </td>
                        <td>
                          <Form.Select
                            value={String(row.numberType ?? 1)}
                            disabled={!edit || sav}
                            onChange={(e) =>
                              setOutboundField(k, { numberType: parseInt(e.target.value, 10) })
                            }>
                            <option value="1">Standard</option>
                            <option value="2">Voicemail</option>
                          </Form.Select>
                        </td>
                        <td>
                          <Button
                            type="button"
                            variant={row.active ? 'success' : 'danger'}
                            size="sm"
                            disabled={!edit || sav}
                            onClick={() => setOutboundField(k, { active: !row.active })}>
                            {row.active ? 'Active' : 'Inactive'}
                          </Button>
                        </td>
                        <td>
                          {edit && (
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              disabled={sav}
                              onClick={() => saveOutbound(row)}>
                              {sav ? 'Saving…' : 'Save'}
                            </Button>
                          )}
                          {!edit && (
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              onClick={() => startEditOutbound(row)}>
                              Edit
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {!outboundLoading && outRows.length === 0 && (
              <p className="text-muted">No outbound numbers.</p>
            )}
          </Col>
          <Col lg={6}>
            <FacilityAnisSection facility={facility} onBusyChange={setAniSectionBusy} />
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default FacilityNumbersTab;
